import { Plus, Trash2 } from "lucide-react";
import usePolicyCompanyRoles from "../policy-wizard/usePolicyCompanyRoles";
import PolicyRoleSelectOptions from "../policy-wizard/PolicyRoleSelectOptions";
import { resolveRoleSelectValue } from "../../../utils/roleSelectOptions";
import { normalizeWorkflow } from "./declarationWorkflowDefaults";

/**
 * Workflow block for Declaration / Proof Submission settings.
 * Roles come from User Roles (same catalog as Onboarding).
 */
export default function DeclarationWorkflowBlock({
  darkMode = false,
  title = "Approval workflow",
  value,
  onChange,
}) {
  const workflow = normalizeWorkflow(value);
  const { policyRoleOptions } = usePolicyCompanyRoles();

  const setWorkflow = (patch) => {
    onChange?.(normalizeWorkflow({ ...workflow, ...patch }));
  };

  const addStep = () => {
    setWorkflow({
      workflowSteps: [
        ...workflow.workflowSteps,
        { role: "", slaDays: 3, notifyOnly: false },
      ],
    });
  };

  const updateStep = (index, patch) => {
    const next = workflow.workflowSteps.map((s, i) =>
      i === index ? { ...s, ...patch } : s
    );
    setWorkflow({ workflowSteps: next });
  };

  const removeStep = (index) => {
    setWorkflow({
      workflowSteps: workflow.workflowSteps.filter((_, i) => i !== index),
    });
  };

  const box = darkMode
    ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/40"
    : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]";

  return (
    <div className={`mt-4 space-y-3 rounded-2xl border p-3 ${box}`}>
      <div>
        <h5 className="text-sm font-semibold">{title}</h5>
      </div>

      <div className="inline-flex flex-wrap gap-1 rounded-full border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] p-1 dark:border-[hsl(var(--ds-border))] dark:bg-[hsl(var(--ds-muted))]">
        {[
          { value: "auto_approved", label: "Auto approved" },
          { value: "workflow", label: "Through workflow" },
        ].map((opt) => {
          const active = workflow.approvalMethod === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => setWorkflow({ approvalMethod: opt.value })}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
                active
                  ? "inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3.5 py-2 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45"
                  : darkMode
                  ? "text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
                  : "bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {workflow.approvalMethod === "workflow" && (
        <div className="space-y-2">
          {workflow.workflowSteps.map((step, index) => {
            const selectValue = step.role
              ? resolveRoleSelectValue(policyRoleOptions, step.role)
              : "";
            return (
              <div
                key={`step-${index}`}
                className={`flex flex-wrap items-end gap-2 rounded-xl border p-2 ${
                  darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
                }`}
              >
                <div className="min-w-[160px] flex-1">
                  <label className="block text-[11px] font-medium text-[hsl(var(--ds-muted-fg))] mb-1">
                    Role (step {index + 1})
                  </label>
                  <select
                    value={selectValue}
                    onChange={(e) => updateStep(index, { role: e.target.value })}
                    className={`w-full rounded-xl border px-2 py-1.5 text-sm ${
                      darkMode
                        ? "bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-white"
                        : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"
                    }`}
                  >
                    <option value="">Select role…</option>
                    <PolicyRoleSelectOptions
                      roleOptions={policyRoleOptions}
                      storedValue={step.role || ""}
                    />
                  </select>
                </div>
                <div className="w-24">
                  <label className="block text-[11px] font-medium text-[hsl(var(--ds-muted-fg))] mb-1">
                    SLA (days)
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={step.slaDays ?? 3}
                    onChange={(e) =>
                      updateStep(index, {
                        slaDays: Math.max(1, Number(e.target.value) || 1),
                      })
                    }
                    className={`w-full rounded-xl border px-2 py-1.5 text-sm ${
                      darkMode
                        ? "bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-white"
                        : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"
                    }`}
                  />
                </div>
                <label className="inline-flex items-center gap-1.5 text-xs mb-2">
                  <input
                    type="checkbox"
                    checked={!!step.notifyOnly}
                    onChange={(e) =>
                      updateStep(index, { notifyOnly: e.target.checked })
                    }
                  />
                  Notify only
                </label>
                <button
                  type="button"
                  onClick={() => removeStep(index)}
                  className="mb-1 inline-flex h-8 w-8 items-center justify-center rounded-full border border-red-200 text-red-600 hover:bg-red-50"
                  aria-label="Remove step"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
          <button
            type="button"
            onClick={addStep}
            className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--ds-border))] px-3 py-1.5 text-xs font-medium hover:bg-[hsl(var(--ds-muted))] dark:border-[hsl(var(--ds-border))] dark:hover:bg-[hsl(var(--ds-muted))]"
          >
            <Plus className="h-3.5 w-3.5" />
            Add step
          </button>
        </div>
      )}
    </div>
  );
}
